"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { useRender } from "@base-ui/react/use-render";
import { Check, Copy } from "lucide-react";
import { toast } from "sonner";

import { writeClipboard } from "@/lib/clipboard";
import { cn } from "@/lib/utils";

type CopyOptions = {

  value: string | (() => string);

  successText?: string;
  errorText?: string;
  timeout?: number;
  disabled?: boolean;
  onCopy?: () => void;
  onError?: (error: Error) => void;
};

export function useClipboardCopy({ value, successText, errorText, timeout = 2000, disabled = false, onCopy, onError }: CopyOptions) {
  const [copied, setCopied] = useState(false);
  const [copying, setCopying] = useState(false);
  const locked = useRef(false);
  const mounted = useRef(true);
  const attempt = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      attempt.current += 1;
      locked.current = false;
      if (timer.current !== null) clearTimeout(timer.current);
    };
  }, []);

  async function copy() {
    if (disabled || locked.current || !mounted.current) return false;
    locked.current = true;
    const currentAttempt = ++attempt.current;
    setCopying(true);
    let success = false;
    let error = new Error(errorText || "Clipboard copy failed");
    try {
      success = await writeClipboard(typeof value === "function" ? value() : value);
    } catch (cause) {
      if (cause instanceof Error) error = cause;
    }
    if (!mounted.current || attempt.current !== currentAttempt) return false;
    setCopying(false);
    if (!success) {
      locked.current = false;
      if (errorText) toast.error(errorText);
      onError?.(error);
      return false;
    }
    setCopied(true);
    timer.current = setTimeout(() => {
      locked.current = false;
      timer.current = null;
      setCopied(false);
    }, timeout);
    if (successText) toast.success(successText);
    onCopy?.();
    return true;
  }

  return { copied, copying, disabled: disabled || copying || copied, copy };
}

type CopyButtonState = { copied: boolean; copying: boolean };

export function CopyButton({
  value,
  label,
  successText,
  errorText,
  className,
  timeout = 2000,
  disabled = false,
  children,
  render,
  copiedLabel,
  onCopy,
  onError,
}: CopyOptions & {

  label: string;
  copiedLabel?: string;
  className?: string;

  children?: (copied: boolean) => ReactNode;
  render?: useRender.RenderProp<CopyButtonState>;
}) {
  const { copied, copying, disabled: inactive, copy } = useClipboardCopy({ value, successText, errorText, timeout, disabled, onCopy, onError });
  return useRender({
    defaultTagName: "button",
    render,
    state: { copied, copying },
    props: {
      type: "button",
      "aria-label": copied ? copiedLabel ?? label : label,
      "aria-busy": copying || undefined,
      "data-copy-state": copying ? "copying" : copied ? "copied" : "idle",
      disabled: inactive,
      className: cn(
        !render && "inline-flex items-center justify-center transition-opacity disabled:cursor-default",
        disabled && "opacity-50",
        className,
      ),
      onClick: () => void copy(),
      children: children
        ? children(copied)
        : copied ? <Check aria-hidden /> : <Copy aria-hidden />,
    },
  });
}

export async function copyTextWithToast(
  text: string,
  successText = "已复制到剪贴板",
): Promise<void> {
  if (await writeClipboard(text)) {
    toast.success(successText);

    return;
  }

  toast.error("复制失败，请手动复制");
}
